import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircle, Info, Plus, Settings2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { DiscordAdvancedSheet } from './DiscordAdvanced.sheet';
import { DiscordAdvancedCallout } from './DiscordAdvancedCallout';
import { DiscordAutomatedMessagesDialog } from './DiscordAutomatedMessages.dialog';
import type {
  DiscordAutomatedMessageRuleKey,
  DiscordAutomatedMessageSaveSource,
} from './DiscordAutomatedMessages.dialog';
import { HealthLine } from './DiscordHealthLine';
import { DiscordServerRow } from './DiscordServerRow';
import {
  DISCORD_INTEGRATION_NAME,
  DISCORD_QUERY_KEY,
  type DiscordIntegrationParams,
  type DiscordIntegrationResponse,
  LATE_CALLBACK_POLL_WINDOW_MS,
  REPLY_MODE_CHANNEL,
  REPLY_MODE_THREADS,
  SYNC_POLL_INTERVAL_MS,
  SYNC_TIMEOUT_MS,
} from './discordIntegration';
import type {
  DiscordAutomatedMessageOptions,
  DiscordAutomatedMessages,
  DiscordChannelToggle,
  DiscordGuildHealth,
  DiscordGuildSummary,
  DiscordTokenStatus,
} from '@/Config/api/queryClient';
import { DiscordChannelToggleError } from '@/Config/api/queryClient';
import { QueryKey } from '@/Config/api/queryKey';
import type { ErrorResponse } from '@/Domain/api/App/dto/Error.dto';
import type { KnowledgeBase } from '@/Domain/api/KnowledgeBase/dto/KnowledgeBase.dto';
import { IntegrationEnabledBadge } from '@/Domain/components/Integration/IntegrationEnabledBadge';
import { IntegrationStatusBadge } from '@/Domain/components/Integration/IntegrationStatusBadge';
import { IntegrationConnectionCard } from '@/Domain/components/Integration/components/IntegrationConnectionCard';
import { IntegrationControlField } from '@/Domain/components/Integration/components/IntegrationControlField';
import { IntegrationIntroduction } from '@/Domain/components/Integration/components/IntegrationIntroduction';
import { IntegrationSection } from '@/Domain/components/Integration/components/IntegrationSection';
import { IntegrationToggleField } from '@/Domain/components/Integration/components/IntegrationToggleField';
import { Alert, AlertDescription } from '@/Domain/components/Shared/Alert/Alert';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import { FieldGroup } from '@/Domain/components/Shared/Field/Field';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { Label } from '@/Domain/components/Shared/Label/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/Domain/components/Shared/Select/Select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/Domain/components/Shared/Sheet/sheet';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import CloseIcon from '@/Domain/components/icons/Close.icon';
import { DiscordIcon } from '@/Domain/components/icons/Discord.icon';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import {
  trackIntegrationConnectFailed,
  trackIntegrationConnectStarted,
} from '@/Domain/hooks/Integration/integrationConnectAnalytics';
import { useControllableState } from '@/Domain/hooks/Shared/useControllableState.hook';
import { cn } from '@/Domain/utils/cn';

export interface DiscordSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Shows the Advanced (own bot) sheet in place of this one; internal state when omitted. */
  advancedOpen?: boolean;
  onAdvancedOpenChange?: (open: boolean) => void;
  /** Palette for the portaled content, which renders outside any themed shell the caller sets up. */
  contentClassName?: string;
}

const DISCORD_NICKNAME_PERMISSION_ERROR_CODE = 'DISCORD_NICKNAME_PERMISSION_DENIED';

type OwnBotChangeScope = 'channel' | 'own_bot';
// Slug must match the docs heading (cross-repo contract, kept in sync with the docs PR).
const DISCORD_COEXISTENCE_DOCS_URL =
  'https://docs.quickchat.ai/channels/discord#both-bots-are-in-the-same-server';

/** One-click Discord connect (canonical "Quickchat AI" app); the legacy own-bot flow lives on in the Advanced sheet. */
export const DiscordSheet = ({
  open,
  onOpenChange,
  advancedOpen: controlledAdvancedOpen,
  onAdvancedOpenChange,
  contentClassName,
}: DiscordSheetProps) => {
  const { t } = useTranslation(['integration', 'integrations']);
  const { capture } = usePostHogAnalytics();
  const queryClient = useQueryClient();
  const [advancedOpen, setAdvancedOpen] = useControllableState(
    controlledAdvancedOpen,
    onAdvancedOpenChange,
    false,
  );

  useEffect(() => {
    if (!open) setAdvancedOpen(false);
  }, [open, setAdvancedOpen]);
  const [confirmRemove, setConfirmRemove] = useState<DiscordGuildSummary | null>(null);
  const [automatedMessagesGuildId, setAutomatedMessagesGuildId] = useState<string | null>(null);
  // Held from the click until the browser leaves, so the button doesn't flip back mid-redirect.
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [connectNickname, setConnectNickname] = useState<string | null>(null);
  // An own-bot start or stop is only real once the gateway calls back, as on the Advanced sheet.
  const [ownBotSync, setOwnBotSync] = useState<{
    enabled: boolean;
    since: number | null;
    late: boolean;
    scope: OwnBotChangeScope;
  } | null>(null);
  const [ownBotRejected, setOwnBotRejected] = useState(false);
  const ownBotSinceRef = useRef<number | null>(null);
  const ownBotScopeRef = useRef<OwnBotChangeScope>('channel');
  const openRef = useRef(open);
  useEffect(() => {
    openRef.current = open;
  }, [open]);

  const {
    data: guilds,
    isLoading: guildsLoading,
    isError: guildsError,
    isFetching: guildsFetching,
    isSuccess: guildsFetchSucceeded,
    isRefetchError: guildsRefetchError,
    refetch: refetchGuilds,
  } = useQuery<DiscordGuildSummary[]>({
    queryKey: [QueryKey.integration.discordGuilds],
    enabled: open,
  });

  // Whether the user also has the advanced own-bot flow set up (token saved, not necessarily running).
  const { data: tokenStatus, isLoading: tokenStatusLoading } = useQuery<DiscordTokenStatus>({
    queryKey: [QueryKey.integration.discordTokenStatus],
    enabled: open,
    // The shared 10-minute default suits has_token, which barely changes; bot_active is a
    // runtime flag the gateway can flip out-of-band, and it decides this sheet's alert severity.
    staleTime: 0,
  });
  // An ACTIVE guild row only means we were never told the bot left, so ask Discord directly.
  // isPending, not isFetching: once a check has settled, a failed or background one must not
  // put the rows back to "Checking with Discord" — the same rule the own-bot line follows.
  const {
    data: guildsHealth,
    isPending: guildsHealthLoading,
    isError: guildsHealthError,
  } = useQuery<{
    guilds: DiscordGuildHealth[];
    checked_at: string;
  }>({
    queryKey: [QueryKey.integration.discordGuildsHealth],
    enabled: open,
  });
  const { data: knowledgeBase } = useQuery<KnowledgeBase>({
    queryKey: [QueryKey.knowledgeBase.get],
    enabled: open,
  });
  // Channel-level settings; they govern the one-click servers above and the advanced bot alike.
  const {
    data: integration,
    isLoading: integrationLoading,
    isError: integrationError,
    isFetching: integrationFetching,
    isSuccess: integrationFetchSucceeded,
    isRefetchError: integrationRefetchError,
    refetch: refetchIntegration,
  } = useQuery<DiscordIntegrationResponse>({
    queryKey: DISCORD_QUERY_KEY,
    enabled: open,
    // The app-wide Infinity would keep showing settings changed elsewhere after a reopen.
    staleTime: 0,
    refetchInterval: (query) =>
      ownBotSync || query.state.data?.discord_sync_pending ? SYNC_POLL_INTERVAL_MS : false,
  });
  // A toggle needs status and servers read during this opening: a cached timestamp could confirm the
  // wrong callback, and a cached server list could leave out a server switched on elsewhere.
  const [statusBaselineReady, setStatusBaselineReady] = useState(false);
  useEffect(() => {
    if (!open) {
      setStatusBaselineReady(false);
      return;
    }
    if (
      !integrationFetching &&
      !integrationRefetchError &&
      integrationFetchSucceeded &&
      !guildsFetching &&
      !guildsRefetchError &&
      guildsFetchSucceeded
    ) {
      setStatusBaselineReady(true);
    }
  }, [
    open,
    integrationFetching,
    integrationFetchSucceeded,
    integrationRefetchError,
    guildsFetching,
    guildsFetchSucceeded,
    guildsRefetchError,
  ]);

  const servers = guilds ?? [];
  // Dropped on error rather than kept: React Query retains the last map, and a membership
  // verdict from a check that has since failed is not one we can still stand behind.
  const guildPresence = useMemo(
    () =>
      new Map(
        guildsHealthError
          ? []
          : (guildsHealth?.guilds ?? []).map((entry) => [entry.guild_id, entry.present]),
      ),
    [guildsHealth, guildsHealthError],
  );
  const automatedMessagesGuild =
    servers.find((guild) => guild.guild_id === automatedMessagesGuildId) ?? null;
  const hasOneClickServers = servers.length > 0;
  // Only ACTIVE + enabled servers actually route to the shared bot, so only those shadow an own
  // bot — and not one Discord has told us the bot was kicked from, or the same screen would
  // show "Bot is no longer in this server" next to a warning that it is answering there.
  const hasLiveOneClickServers = servers.some(
    (guild) =>
      guild.state === 'ACTIVE' && guild.enabled && guildPresence.get(guild.guild_id) !== false,
  );
  const params = integration?.params;
  // The switches read the own bot from the gated status read, so a failed token-status read cannot drop it.
  const ownBotSaved = Boolean(params?.discord_bot_token);
  const ownBotRunning = ownBotSaved && Boolean(params?.discord_bot_active);
  // Both reads serve the same row, so either one is enough to show an own bot is set up.
  const advancedConnected = Boolean(tokenStatus?.has_token) || ownBotSaved;
  // Same rule as the Advanced sheet's `ownBotShadowed`, so one setup cannot read as a warning
  // on one screen and as a note on the other: a saved-but-off own bot is not being shadowed.
  const ownBotShadowed =
    advancedConnected && Boolean(tokenStatus?.bot_active) && hasLiveOneClickServers;
  const isConnected = hasOneClickServers || advancedConnected;
  const isInitialLoading = guildsLoading || tokenStatusLoading || integrationLoading;
  // No channel-wide flag exists: Discord is on while any server or the own bot is.
  const channelEnabled =
    servers.some((guild) => guild.state === 'ACTIVE' && guild.enabled) || ownBotRunning;
  // The server refuses Discord saves until an unresolved gateway push clears.
  const serverSyncPending = Boolean(integration?.discord_sync_pending);
  const serverSyncWasPendingRef = useRef(false);
  // A push that resolves late can change the own bot's state behind a cached token status.
  useEffect(() => {
    if (serverSyncPending) {
      serverSyncWasPendingRef.current = true;
      return;
    }
    if (!serverSyncWasPendingRef.current) return;
    serverSyncWasPendingRef.current = false;
    void queryClient.invalidateQueries({ queryKey: [QueryKey.integration.discordTokenStatus] });
  }, [serverSyncPending, queryClient]);
  const suggestedConnectNickname = (knowledgeBase?.one_word_description ?? '').trim().slice(0, 32);
  const pendingConnectNickname = connectNickname ?? suggestedConnectNickname;
  // A working own-bot setup must not be presented as an empty state to upsell over.
  const leadWithAdvanced = advancedConnected && !hasOneClickServers;

  useEffect(() => {
    if (!open) {
      setConfirmRemove(null);
      setAutomatedMessagesGuildId(null);
      setConnectNickname(null);
      setOwnBotSync(null);
      setOwnBotRejected(false);
    }
  }, [open]);

  // Once per open, when the one-click view is first shown: a caller can open straight into Advanced.
  const oneClickOpenCapturedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      oneClickOpenCapturedRef.current = false;
      return;
    }
    if (advancedOpen || oneClickOpenCapturedRef.current) return;
    oneClickOpenCapturedRef.current = true;
    capture(PostHogEventEnum.DiscordIntegrationOneClickSheetOpened);
  }, [open, advancedOpen, capture]);

  // isRedirecting is deliberately never reset on the success path; a bfcache restore brings it back with the page.
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) setIsRedirecting(false);
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  const oauthMutation = useMutation<{ url: string }, Error, { nickname: string }>({
    mutationKey: [QueryKey.integration.discordOauth],
    onSuccess: (result) => {
      if (!result?.url) {
        setIsRedirecting(false);
        trackIntegrationConnectFailed(capture, 'discord', 'oauth_failed');
        toast.error(t('integration:discord.oneclick.oauth_failed'));
        return;
      }
      // Full-page redirect, not a popup (blocked); the terminal event fires on return.
      window.location.href = result.url;
    },
    onError: () => {
      setIsRedirecting(false);
      trackIntegrationConnectFailed(capture, 'discord', 'oauth_failed');
      toast.error(t('integration:discord.oneclick.oauth_failed'));
    },
  });

  const startAuthorize = () => {
    const nickname = pendingConnectNickname.trim().slice(0, 32);
    capture(PostHogEventEnum.DiscordIntegrationAuthorizeOpened, {
      has_nickname: nickname !== '',
      nickname_edited: connectNickname !== null,
    });
    trackIntegrationConnectStarted(capture, 'discord');
    setIsRedirecting(true);
    oauthMutation.mutate({ nickname });
  };

  const guildPatchMutation = useMutation<
    DiscordGuildSummary,
    AxiosError<ErrorResponse>,
    { guildId: string; enabled?: boolean; nickname?: string }
  >({
    mutationKey: [QueryKey.integration.discordGuildPatch],
    onSuccess: (updated, variables) => {
      if (variables.nickname !== undefined && updated.nickname === undefined) {
        toast.error(t('integration:discord.oneclick.nickname_failed'));
        void queryClient.invalidateQueries({ queryKey: [QueryKey.integration.discordGuilds] });
        return;
      }
      queryClient.setQueryData<DiscordGuildSummary[]>(
        [QueryKey.integration.discordGuilds],
        (prev) =>
          prev?.map((guild) =>
            guild.guild_id === updated.guild_id
              ? {
                  ...updated,
                  automated_messages: updated.automated_messages ?? guild.automated_messages,
                }
              : guild,
          ) ?? prev,
      );
      if (variables.nickname !== undefined) {
        capture(PostHogEventEnum.DiscordIntegrationNicknameSaved, {
          cleared: variables.nickname === '',
          source: 'settings',
        });
      }
      toast.success(
        t(
          variables.nickname === undefined
            ? 'integration:discord.oneclick.saved'
            : 'integration:discord.oneclick.nickname_saved',
        ),
      );
    },
    onError: (error, variables) => {
      if (variables.nickname !== undefined) {
        const errorCode = error.response?.data?.errors?.root?.[0]?.code;
        toast.error(
          t(
            errorCode === DISCORD_NICKNAME_PERMISSION_ERROR_CODE
              ? 'integration:discord.oneclick.nickname_permission_failed'
              : 'integration:discord.oneclick.nickname_failed',
          ),
        );
      } else {
        toast.error(t('integration:discord.oneclick.save_failed'));
      }
      void queryClient.invalidateQueries({ queryKey: [QueryKey.integration.discordGuilds] });
    },
  });

  const guildRemoveMutation = useMutation<void, AxiosError, { guildId: string }>({
    mutationKey: [QueryKey.integration.discordGuildDelete],
    onSuccess: (_data, variables) => {
      queryClient.setQueryData<DiscordGuildSummary[]>(
        [QueryKey.integration.discordGuilds],
        (prev) => prev?.filter((g) => g.guild_id !== variables.guildId) ?? prev,
      );
      // Removing the last server can drop the AI Action System Token gate.
      void queryClient.invalidateQueries({ queryKey: [QueryKey.integration.discordTokenStatus] });
      setConfirmRemove(null);
      toast.success(t('integration:discord.oneclick.removed'));
    },
    onError: () => {
      toast.error(t('integration:discord.oneclick.remove_failed'));
    },
  });

  // Reply mode and search are read per message, so there is no gateway callback to await.
  const settingsMutation = useMutation<
    DiscordIntegrationResponse,
    AxiosError<ErrorResponse>,
    { name: typeof DISCORD_INTEGRATION_NAME; params: Partial<DiscordIntegrationParams> }
  >({
    mutationKey: [QueryKey.integration.patch],
    onSuccess: (responseData) => {
      queryClient.setQueryData<DiscordIntegrationResponse>(DISCORD_QUERY_KEY, responseData);
    },
    onError: (error) => {
      void queryClient.invalidateQueries({ queryKey: DISCORD_QUERY_KEY });
      toast.error(
        error.response?.data?.errors?.root?.[0]?.message || t('integration:discord.save_failed'),
      );
    },
  });

  // Shows an in-flight save's value, as the form-backed controls these replaced did.
  const pendingSettings = settingsMutation.isPending
    ? settingsMutation.variables?.params
    : undefined;
  const shownSettings: Partial<DiscordIntegrationParams> = { ...params, ...pendingSettings };

  const saveSettings = (partial: Partial<DiscordIntegrationParams>) => {
    settingsMutation.mutate(
      { name: DISCORD_INTEGRATION_NAME, params: partial },
      { onSuccess: () => toast.success(t('integration:discord.saved')) },
    );
  };

  const channelMutation = useMutation<void, DiscordChannelToggleError, DiscordChannelToggle>({
    mutationKey: [QueryKey.integration.discordChannelToggle],
    onSuccess: (_data, { enabled, ownBot }) => {
      if (ownBot) {
        // The own-bot PATCH can wait up to 70s on the gateway; a sheet closed meanwhile rereads on reopen.
        if (openRef.current) {
          setOwnBotSync({
            enabled,
            since: ownBotSinceRef.current,
            late: false,
            scope: ownBotScopeRef.current,
          });
        }
        return;
      }
      const name = t('integration:discord.title');
      toast.success(
        enabled
          ? t('integrations:toast.enabled', { name })
          : t('integrations:toast.disabled', { name }),
      );
    },
    onError: (error, { enabled }) => {
      const failure = (
        error instanceof DiscordChannelToggleError ? error.failure : error
      ) as AxiosError<ErrorResponse>;
      // The server's own message matters here: a 503 means the change saved but Discord has not applied it.
      toast.error(
        failure?.response?.data?.errors?.root?.[0]?.message || t('integration:discord.save_failed'),
      );
      // A server write failed, but an own-bot write that landed still awaits the gateway.
      if (error instanceof DiscordChannelToggleError && error.ownBotApplied && openRef.current) {
        setOwnBotSync({
          enabled,
          since: ownBotSinceRef.current,
          late: false,
          scope: ownBotScopeRef.current,
        });
      }
    },
    // Awaited, so the switch stays locked until refetched state replaces the requested one.
    onSettled: (_data, _error, { ownBot }) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: [QueryKey.integration.discordGuilds] }),
        ...(ownBot
          ? [
              queryClient.invalidateQueries({
                queryKey: [QueryKey.integration.discordTokenStatus],
              }),
              queryClient.invalidateQueries({ queryKey: DISCORD_QUERY_KEY }),
            ]
          : []),
      ]),
  });

  // Finishes an own-bot start or stop once the gateway reports back on it.
  useEffect(() => {
    if (!ownBotSync || !params?.discord_last_updated) return;
    const reportedAt = Date.parse(params.discord_last_updated);
    if (ownBotSync.since !== null && reportedAt <= ownBotSync.since) return;
    const failed = Boolean(params.discord_bot_errors);
    if (!failed && params.discord_bot_active !== ownBotSync.enabled) return;
    setOwnBotSync(null);
    // Same row as token status, so its reported state stands in until the refetch lands.
    queryClient.setQueryData<DiscordTokenStatus>(
      [QueryKey.integration.discordTokenStatus],
      (prev) => prev && { ...prev, bot_active: params.discord_bot_active },
    );
    void queryClient.invalidateQueries({ queryKey: [QueryKey.integration.discordTokenStatus] });
    if (failed) {
      setOwnBotRejected(true);
      toast.error(t('integration:discord.errors.heading'));
      return;
    }
    if (ownBotSync.scope === 'own_bot') {
      toast.success(t('integration:discord.saved'));
      return;
    }
    const name = t('integration:discord.title');
    toast.success(
      ownBotSync.enabled
        ? t('integrations:toast.enabled', { name })
        : t('integrations:toast.disabled', { name }),
    );
  }, [
    ownBotSync,
    params?.discord_last_updated,
    params?.discord_bot_errors,
    params?.discord_bot_active,
    queryClient,
    t,
  ]);

  // Past the timeout the change stays watched and locked for a late callback, as on the Advanced sheet.
  useEffect(() => {
    if (!ownBotSync) return;
    const timeoutId = setTimeout(
      () => {
        if (ownBotSync.late) {
          setOwnBotSync(null);
          void queryClient.invalidateQueries({
            queryKey: [QueryKey.integration.discordTokenStatus],
          });
          return;
        }
        toast.info(t('integration:discord.sync_timeout'));
        setOwnBotSync({ ...ownBotSync, late: true });
      },
      ownBotSync.late ? LATE_CALLBACK_POLL_WINDOW_MS : SYNC_TIMEOUT_MS,
    );
    return () => clearTimeout(timeoutId);
  }, [ownBotSync, queryClient, t]);

  const pendingToggle = channelMutation.isPending ? channelMutation.variables : undefined;
  // The own bot's requested state while its write or the gateway's confirmation is outstanding.
  const ownBotTarget = pendingToggle?.ownBot ? pendingToggle.enabled : ownBotSync?.enabled;
  const ownBotShownOn = ownBotTarget ?? ownBotRunning;
  const shownChannelEnabled =
    servers.some(
      (guild) =>
        guild.state === 'ACTIVE' &&
        (pendingToggle?.guildIds.includes(guild.guild_id) ? pendingToggle.enabled : guild.enabled),
    ) ||
    (ownBotSaved && ownBotShownOn);
  const channelBusy = channelMutation.isPending || ownBotSync !== null;
  // A switch-off picks its servers from the cached list, so it waits out any re-read in progress.
  const settingsBusy =
    settingsMutation.isPending ||
    channelBusy ||
    serverSyncPending ||
    !statusBaselineReady ||
    guildsFetching ||
    integrationFetching;

  const runToggle = (toggle: DiscordChannelToggle, scope: OwnBotChangeScope) => {
    capture(PostHogEventEnum.DiscordIntegrationBotToggled, { enabled: toggle.enabled });
    setOwnBotRejected(false);
    ownBotScopeRef.current = scope;
    ownBotSinceRef.current = params?.discord_last_updated
      ? Date.parse(params.discord_last_updated)
      : null;
    channelMutation.mutate(toggle);
  };

  const toggleChannel = (enabled: boolean) =>
    runToggle(
      {
        enabled,
        guildIds: servers
          .filter((guild) => guild.state === 'ACTIVE' && guild.enabled !== enabled)
          .map((guild) => guild.guild_id),
        ownBot: ownBotSaved && ownBotRunning !== enabled,
      },
      'channel',
    );

  const toggleOwnBot = (enabled: boolean) =>
    runToggle({ enabled, guildIds: [], ownBot: true }, 'own_bot');
  // Only when the master switch cannot help: the own bot is stopped while a server keeps Discord on.
  // It stays while its own change is in flight, so the click keeps its spinner and outcome.
  const showOwnBotSwitch =
    ownBotSaved &&
    ((!ownBotRunning && servers.some((guild) => guild.state === 'ACTIVE' && guild.enabled)) ||
      (pendingToggle !== undefined && ownBotScopeRef.current === 'own_bot') ||
      ownBotSync?.scope === 'own_bot');
  // Read live, so a bot fixed or removed in the advanced setup drops the alert on the way back.
  const ownBotStopped = ownBotSaved && Boolean(params?.discord_bot_errors);
  const showOwnBotRejected = ownBotRejected && !ownBotRunning && ownBotStopped;

  const {
    data: automatedMessageOptions,
    isLoading: automatedMessageOptionsLoading,
    isError: automatedMessageOptionsError,
    refetch: refetchAutomatedMessageOptions,
  } = useQuery<DiscordAutomatedMessageOptions>({
    queryKey: [QueryKey.integration.discordAutomatedMessageOptions, automatedMessagesGuildId ?? ''],
    enabled: open && automatedMessagesGuildId !== null,
  });

  const automatedMessagesMutation = useMutation<
    DiscordAutomatedMessages,
    AxiosError<ErrorResponse>,
    {
      guildId: string;
      automatedMessages: DiscordAutomatedMessages;
      rule: DiscordAutomatedMessageRuleKey;
      source: DiscordAutomatedMessageSaveSource;
    }
  >({
    mutationKey: [QueryKey.integration.discordAutomatedMessagesUpdate],
    onSuccess: (automatedMessages, variables) => {
      queryClient.setQueryData<DiscordGuildSummary[]>(
        [QueryKey.integration.discordGuilds],
        (previous) =>
          previous?.map((guild) =>
            guild.guild_id === variables.guildId
              ? { ...guild, automated_messages: automatedMessages }
              : guild,
          ) ?? previous,
      );
      capture(PostHogEventEnum.DiscordIntegrationAutomatedMessagesSaved, {
        channel_created_enabled: automatedMessages.channel_created.enabled,
        member_joined_enabled: automatedMessages.member_joined.enabled,
        thread_created_enabled: automatedMessages.thread_created.enabled,
        saved_rule: variables.rule,
        source: variables.source,
      });
      toast.success(t('integration:discord.oneclick.automated_messages.saved'));
    },
    onError: (error) => {
      // A rejection usually means the cached list is stale, so retrying it unchanged would fail again.
      void queryClient.invalidateQueries({
        queryKey: [QueryKey.integration.discordAutomatedMessageOptions],
      });
      // Prefer the server's message: it names the selection Discord no longer has.
      const reason = error.response?.data?.errors?.root?.[0]?.message;
      toast.error(reason || t('integration:discord.oneclick.automated_messages.save_failed'));
    },
  });

  const handleToggleEnabled = (guildId: string, enabled: boolean) => {
    capture(PostHogEventEnum.DiscordIntegrationServerToggled, { enabled });
    guildPatchMutation.mutate({ guildId, enabled });
  };

  const handleSaveNickname = async (
    guildId: string,
    nickname: string,
  ): Promise<string | undefined> => {
    try {
      const updated = await guildPatchMutation.mutateAsync({ guildId, nickname });
      return updated.nickname;
    } catch {
      return undefined;
    }
  };

  const handleRemoveConfirm = () => {
    if (!confirmRemove) return;
    capture(PostHogEventEnum.DiscordIntegrationServerRemoved);
    guildRemoveMutation.mutate({ guildId: confirmRemove.guild_id });
  };

  const handleOpenAutomatedMessages = (guild: DiscordGuildSummary) => {
    capture(PostHogEventEnum.DiscordIntegrationAutomatedMessagesOpened);
    setAutomatedMessagesGuildId(guild.guild_id);
  };

  // Each rule saves on its own and the dialog stays open, so it needs the outcome back.
  const handleSaveAutomatedMessages = async (
    guildId: string,
    automatedMessages: DiscordAutomatedMessages,
    meta: { rule: DiscordAutomatedMessageRuleKey; source: DiscordAutomatedMessageSaveSource },
  ): Promise<boolean> => {
    try {
      await automatedMessagesMutation.mutateAsync({ guildId, automatedMessages, ...meta });
      return true;
    } catch {
      return false;
    }
  };

  // The guild currently mid-mutation, so only its own row's controls disable.
  const pendingGuildId = guildPatchMutation.isPending
    ? guildPatchMutation.variables?.guildId
    : guildRemoveMutation.isPending
      ? guildRemoveMutation.variables?.guildId
      : undefined;

  const connectControls = (buttonTestId: string, buttonLabel: string) => (
    <div className="space-y-4">
      <div className="space-y-3">
        <Label htmlFor={`${buttonTestId}-nickname`}>
          {t('integration:discord.oneclick.connect_nickname_label')}
        </Label>
        <Input
          id={`${buttonTestId}-nickname`}
          value={pendingConnectNickname}
          maxLength={32}
          autoComplete="off"
          placeholder={t('integration:discord.oneclick.nickname_placeholder')}
          disabled={isRedirecting}
          className="w-full bg-card"
          onChange={(event) => setConnectNickname(event.target.value)}
          data-testid="discord-connect-nickname"
        />
        <p className="m-0 text-xs text-muted-foreground">
          {t('integration:discord.oneclick.connect_nickname_help')}
        </p>
      </div>
      <Button
        type="button"
        variant="dark"
        size="sm"
        className="w-full"
        onClick={startAuthorize}
        disabled={isRedirecting}
        data-testid={buttonTestId}
      >
        {isRedirecting ? <Spinner className="size-4" /> : <Plus className="size-4" />}
        {isRedirecting ? t('integration:discord.oneclick.authorizing') : buttonLabel}
      </Button>
    </div>
  );

  const settingsBlock = isConnected ? (
    <IntegrationSection
      title={t('integrations:section.settings')}
      data-testid="discord-settings-section"
    >
      {integrationError && !statusBaselineReady ? (
        <Alert variant="destructive" data-testid="discord-settings-load-error">
          <AlertCircle className="size-4 !text-destructive" />
          <AlertDescription className="space-y-3">
            <p className="m-0">{t('integration:discord.settings_load_failed')}</p>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => void refetchIntegration()}
              data-testid="discord-settings-load-retry"
            >
              {t('integration:discord.oneclick.retry')}
            </Button>
          </AlertDescription>
        </Alert>
      ) : (
        <FieldGroup className="gap-4">
          {(serverSyncPending || ownBotSync?.late) && (
            <Alert variant="info" data-testid="discord-settings-sync-pending-alert">
              <Info className="size-4 !text-info" />
              <AlertDescription>{t('integration:discord.sync_timeout')}</AlertDescription>
            </Alert>
          )}
          <IntegrationToggleField
            id="discord-enable-switch"
            label={t('integrations:enable', { name: 'Discord' })}
            description={t('integration:discord.channel_active.description')}
            checked={shownChannelEnabled}
            onCheckedChange={toggleChannel}
            disabled={settingsBusy || guildPatchMutation.isPending}
            loading={channelBusy}
            data-testid="discord-enable-switch"
          />
          {showOwnBotRejected && (
            <Alert variant="destructive" data-testid="discord-own-bot-rejected-alert">
              <AlertCircle className="size-4 !text-destructive" />
              <AlertDescription className="space-y-3">
                <p className="m-0">{t('integration:discord.errors.own_bot_hint')}</p>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => setAdvancedOpen(true)}
                  data-testid="discord-own-bot-rejected-manage"
                >
                  <Settings2 className="size-4" />
                  {t('integration:discord.oneclick.advanced_connection.manage')}
                </Button>
              </AlertDescription>
            </Alert>
          )}

          <IntegrationToggleField
            id="discord-cross-channel-switch"
            label={t('integration:discord.cross_channel_search.label')}
            tooltip={t('integration:discord.cross_channel_search.description')}
            checked={shownSettings.discord_cross_channel_search_active ?? true}
            onCheckedChange={(checked) => {
              capture(PostHogEventEnum.DiscordIntegrationCrossChannelToggled, { enabled: checked });
              saveSettings({ discord_cross_channel_search_active: checked });
            }}
            disabled={settingsBusy}
            loading={pendingSettings?.discord_cross_channel_search_active !== undefined}
            data-testid="discord-cross-channel-switch"
          />

          <IntegrationControlField
            id="discord-reply-mode"
            label={t('integration:discord.reply_mode.label')}
          >
            <Select
              value={
                shownSettings.discord_respond_in_threads ? REPLY_MODE_THREADS : REPLY_MODE_CHANNEL
              }
              disabled={settingsBusy}
              onValueChange={(value) => {
                const respondInThreads = value === REPLY_MODE_THREADS;
                capture(PostHogEventEnum.DiscordIntegrationReplyModeChanged, {
                  respond_in_threads: respondInThreads,
                });
                saveSettings({ discord_respond_in_threads: respondInThreads });
              }}
            >
              <SelectTrigger
                id="discord-reply-mode"
                data-testid="discord-reply-mode-trigger"
                className="w-full"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className={contentClassName} data-testid="discord-reply-mode-content">
                <SelectItem value={REPLY_MODE_CHANNEL} data-testid="discord-reply-mode-channel">
                  {t('integration:discord.reply_mode.channel')}
                </SelectItem>
                <SelectItem value={REPLY_MODE_THREADS} data-testid="discord-reply-mode-threads">
                  {t('integration:discord.reply_mode.threads')}
                </SelectItem>
              </SelectContent>
            </Select>
          </IntegrationControlField>
        </FieldGroup>
      )}
    </IntegrationSection>
  ) : null;

  const advancedBlock = advancedConnected ? (
    <IntegrationSection
      title={t('integration:discord.oneclick.advanced_connection.title')}
      data-testid="discord-advanced-connection-section"
    >
      <IntegrationConnectionCard
        data-testid="discord-advanced-connection-card"
        details={[
          {
            label: t('integration:discord.oneclick.advanced_connection.label'),
            value: t('integration:discord.oneclick.advanced_connection.value'),
            'data-testid': 'discord-advanced-connection-type',
          },
        ]}
        actions={
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="flex-1"
            onClick={() => setAdvancedOpen(true)}
            data-testid="discord-manage-advanced-button"
          >
            <Settings2 className="size-4" />
            {t('integration:discord.oneclick.advanced_connection.manage')}
          </Button>
        }
      >
        {showOwnBotSwitch && (
          <IntegrationToggleField
            id="discord-own-bot-switch"
            label={t('integration:discord.oneclick.advanced_connection.active_label')}
            checked={ownBotShownOn}
            onCheckedChange={toggleOwnBot}
            disabled={settingsBusy || guildPatchMutation.isPending}
            loading={ownBotTarget !== undefined}
            data-testid="discord-own-bot-switch"
          />
        )}
        {ownBotStopped ? (
          <HealthLine
            tone="destructive"
            label={t('integrations:status.not_running')}
            data-testid="discord-own-bot-stopped"
          />
        ) : (
          <p
            className="m-0 text-sm text-muted-foreground text-pretty"
            data-testid="discord-advanced-connection-note"
          >
            {t('integration:discord.oneclick.advanced_active_note')}
          </p>
        )}
        {hasLiveOneClickServers && (
          // Mirrors the Advanced sheet's coexistence alert so the same conflict reads the
          // same way from either side, instead of a quiet paragraph the user scrolls past.
          <Alert
            variant={ownBotShadowed ? 'warning' : 'info'}
            data-testid="discord-advanced-precedence-note"
            data-severity={ownBotShadowed ? 'warning' : 'info'}
          >
            {ownBotShadowed ? (
              <AlertCircle className="size-4 !text-warning" />
            ) : (
              <Info className="size-4 !text-info" />
            )}
            <AlertDescription>
              <Trans
                t={t}
                i18nKey="integration:discord.oneclick.advanced_connection.precedence_note"
                components={[
                  <a
                    key="docs"
                    href={DISCORD_COEXISTENCE_DOCS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link-underline underline-offset-2 transition-opacity hover:opacity-70"
                  />,
                ]}
              />
            </AlertDescription>
          </Alert>
        )}
      </IntegrationConnectionCard>
    </IntegrationSection>
  ) : (
    <DiscordAdvancedCallout onOpenAdvanced={() => setAdvancedOpen(true)} />
  );

  return (
    <>
      <Sheet open={open && !advancedOpen} onOpenChange={onOpenChange}>
        <SheetContent
          className={cn('max-w-xl w-full h-full pb-0', contentClassName)}
          data-testid="discord-sheet"
          onEscapeKeyDown={(e) => {
            e.preventDefault();
            onOpenChange(false);
          }}
        >
          <SheetHeader className="px-0.5 items-center flex-row">
            <div className="flex items-center gap-3">
              <DiscordIcon className="size-8 shrink-0" />
              <div className="flex items-center gap-2">
                <SheetTitle data-testid="discord-sheet-title">
                  {t('integration:discord.title')}
                </SheetTitle>
                {!isInitialLoading && (
                  <>
                    <IntegrationStatusBadge connected={isConnected} />
                    {isConnected && <IntegrationEnabledBadge enabled={channelEnabled} />}
                  </>
                )}
              </div>
            </div>
            <IconButton
              onClick={() => onOpenChange(false)}
              variant="secondary"
              aria-label={t('integrations:close')}
              data-testid="discord-close-button"
              className="shrink-0"
            >
              <CloseIcon />
            </IconButton>
          </SheetHeader>

          <div className="overflow-y-auto px-0.5 pb-6 space-y-6 flex-1">
            <IntegrationIntroduction
              description={t('integration:discord.overview')}
              prerequisites={
                t('integration:discord.oneclick.prerequisites', {
                  returnObjects: true,
                }) as string[]
              }
            />

            {isInitialLoading ? (
              <div className="flex justify-center py-8" data-testid="discord-loading">
                <Spinner />
              </div>
            ) : guildsError ? (
              <Alert variant="destructive" data-testid="discord-load-error">
                <AlertCircle className="size-4 !text-destructive" />
                <AlertDescription className="space-y-3">
                  <p className="m-0">{t('integration:discord.oneclick.load_failed')}</p>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => void refetchGuilds()}
                    data-testid="discord-load-retry"
                  >
                    {t('integration:discord.oneclick.retry')}
                  </Button>
                </AlertDescription>
              </Alert>
            ) : (
              <>
                {leadWithAdvanced && advancedBlock}
                {hasOneClickServers ? (
                  <IntegrationSection
                    title={t('integration:discord.oneclick.connected_servers')}
                    data-testid="discord-servers-section"
                  >
                    <div className="flex flex-col">
                      <AnimatePresence initial={false}>
                        {servers.map((guild) => (
                          <motion.div
                            key={guild.guild_id}
                            layout
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2, ease: 'easeOut' }}
                            className="overflow-hidden"
                          >
                            <div className="pb-3">
                              <DiscordServerRow
                                guild={guild}
                                onToggleEnabled={handleToggleEnabled}
                                onRemove={setConfirmRemove}
                                onSaveNickname={handleSaveNickname}
                                onConfigureAutomatedMessages={handleOpenAutomatedMessages}
                                disabled={
                                  pendingGuildId === guild.guild_id || channelMutation.isPending
                                }
                                present={guildPresence.get(guild.guild_id)}
                                healthLoading={guildsHealthLoading}
                              />
                            </div>
                          </motion.div>
                        ))}
                      </AnimatePresence>
                    </div>
                    {connectControls(
                      'discord-add-server-button',
                      t('integration:discord.oneclick.add_another'),
                    )}
                  </IntegrationSection>
                ) : (
                  <IntegrationSection
                    title={t('integrations:section.connect')}
                    description={
                      leadWithAdvanced
                        ? t('integration:discord.oneclick.also_add_oneclick')
                        : undefined
                    }
                    data-testid="discord-connect-section"
                  >
                    {connectControls(
                      'discord-connect-button',
                      t('integration:discord.oneclick.add_button'),
                    )}
                  </IntegrationSection>
                )}
                {settingsBlock}
                {!leadWithAdvanced && advancedBlock}
              </>
            )}
          </div>

          {/* Remove-server confirmation — cloned from the SlackV2 disconnect dialog.
              `absolute inset-0` centers it within the Sheet, not the viewport. */}
          <AlertDialogPrimitive.Root
            open={confirmRemove !== null}
            onOpenChange={(o) => !o && setConfirmRemove(null)}
          >
            <AlertDialogPrimitive.Overlay className="!mt-0 absolute inset-0 bg-black/30 backdrop-blur-sm z-20" />
            <AlertDialogPrimitive.Content
              className="absolute inset-0 flex items-center justify-center p-4 z-30 focus:outline-none"
              data-testid="discord-remove-dialog"
            >
              <div className="bg-card rounded-lg p-6 max-w-md w-full shadow-lg space-y-4">
                <AlertDialogPrimitive.Title className="text-lg font-semibold text-foreground">
                  {t('integration:discord.oneclick.remove_confirm.title', {
                    server: confirmRemove?.guild_name ?? '',
                  })}
                </AlertDialogPrimitive.Title>
                <AlertDialogPrimitive.Description className="text-sm text-muted-foreground">
                  {t('integration:discord.oneclick.remove_confirm.description')}
                </AlertDialogPrimitive.Description>
                {advancedConnected && (
                  <p
                    className="m-0 text-sm text-muted-foreground"
                    data-testid="discord-remove-advanced-note"
                  >
                    {t('integration:discord.oneclick.remove_confirm.advanced_takeover')}
                  </p>
                )}
                <div className="flex items-center justify-end gap-2 pt-2">
                  <AlertDialogPrimitive.Cancel asChild>
                    <Button variant="secondary" size="md" data-testid="discord-remove-cancel">
                      {t('integration:discord.oneclick.remove_confirm.cancel')}
                    </Button>
                  </AlertDialogPrimitive.Cancel>
                  <Button
                    variant="danger"
                    size="md"
                    onClick={handleRemoveConfirm}
                    disabled={guildRemoveMutation.isPending}
                    data-testid="discord-remove-confirm"
                  >
                    {t('integration:discord.oneclick.remove_confirm.confirm')}
                  </Button>
                </div>
              </div>
            </AlertDialogPrimitive.Content>
          </AlertDialogPrimitive.Root>

          <DiscordAutomatedMessagesDialog
            open={automatedMessagesGuild !== null}
            guild={automatedMessagesGuild}
            options={automatedMessageOptions}
            optionsLoading={automatedMessageOptionsLoading}
            optionsError={automatedMessageOptionsError}
            saving={automatedMessagesMutation.isPending}
            onOpenChange={(dialogOpen) => {
              if (!dialogOpen) setAutomatedMessagesGuildId(null);
            }}
            onRetryOptions={() => void refetchAutomatedMessageOptions()}
            onSave={handleSaveAutomatedMessages}
          />
        </SheetContent>
      </Sheet>

      {/* Sibling of <Sheet>, so it portals on its own and needs the palette handed down again. */}
      <DiscordAdvancedSheet
        open={open && advancedOpen}
        onOpenChange={(next) => !next && onOpenChange(false)}
        onBack={() => setAdvancedOpen(false)}
        contentClassName={contentClassName}
      />
    </>
  );
};

export default DiscordSheet;
